import { useState } from "react";

const palette = {
  bg: "#0B0F1A",
  surface: "#131929",
  raised: "#1A2235",
  border: "#1E2D44",
  accent: "#F5A623",
  accentDim: "#2A1F0A",
  accentText: "#F5A623",
  text: "#EDF0F7",
  muted: "#7A8BA8",
  faint: "#3D4F6A",
  success: "#22C97A",
  successDim: "#0A2218",
  danger: "#FF5C7A",
  dangerDim: "#2A0E16",
  warning: "#FFB547",
  warningDim: "#251A06",
  info: "#4B9EFF",
  infoDim: "#0A1A2E",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  .cb-pp {
    background: ${palette.bg};
    color: ${palette.text};
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    line-height: 1.7;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  /* cage bars background */
  .cb-bars-bg {
    position: fixed;
    inset: 0;
    background-image: repeating-linear-gradient(
      90deg,
      ${palette.border}22 0px,
      ${palette.border}22 2px,
      transparent 2px,
      transparent 60px
    );
    pointer-events: none;
    z-index: 0;
    opacity: 0.5;
  }

  .cb-glow {
    position: fixed;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 400px;
    background: radial-gradient(ellipse, ${palette.accent}18 0%, transparent 65%);
    pointer-events: none;
    z-index: 0;
    animation: cb-pulse 5s ease-in-out infinite alternate;
  }

  @keyframes cb-pulse {
    from { opacity: 0.6; }
    to   { opacity: 1; }
  }

  .cb-wrap {
    position: relative;
    z-index: 1;
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px 80px;
  }

  /* Hero */
  .cb-hero {
    text-align: center;
    padding: 56px 0 44px;
    animation: cb-fadeup .6s ease both;
  }

  .cb-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}44;
    border-radius: 100px;
    padding: 5px 14px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    color: ${palette.accent};
    text-transform: uppercase;
    margin-bottom: 20px;
  }

  .cb-hero h1 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(30px, 6vw, 48px);
    font-weight: 700;
    letter-spacing: -1px;
    line-height: 1.1;
    margin-bottom: 12px;
  }

  .cb-hero h1 em { color: ${palette.accent}; font-style: normal; }

  .cb-hero-sub {
    color: ${palette.muted};
    font-size: 14px;
    max-width: 400px;
    margin: 0 auto 18px;
  }

  .cb-meta {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px 20px;
    color: ${palette.faint};
    font-size: 12px;
  }

  .cb-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  /* Divider */
  .cb-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, ${palette.border}, transparent);
    margin: 36px 0;
  }

  /* Section */
  .cb-section {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 16px;
    padding: 24px 26px 20px;
    margin-bottom: 12px;
    transition: border-color .2s;
    animation: cb-fadeup .5s ease both;
  }

  .cb-section:hover { border-color: ${palette.accent}30; }

  .cb-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  .cb-icon {
    width: 36px; height: 36px; min-width: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .cb-section h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -.1px;
  }

  .cb-section p, .cb-section li {
    color: ${palette.muted};
    font-size: 14px;
    line-height: 1.75;
  }

  .cb-section ul {
    padding-left: 18px;
    margin-top: 8px;
  }

  .cb-section li { margin-bottom: 5px; }

  .cb-section p + p { margin-top: 10px; }

  .cb-section a {
    color: ${palette.info};
    text-decoration: none;
    border-bottom: 1px solid ${palette.info}44;
    transition: border-color .15s;
  }

  .cb-section a:hover { border-color: ${palette.info}; }

  /* callout boxes */
  .cb-box {
    border-radius: 10px;
    padding: 11px 15px;
    margin-top: 14px;
    font-size: 13px;
    display: flex;
    gap: 9px;
    align-items: flex-start;
    line-height: 1.6;
  }

  .cb-box-success {
    background: ${palette.successDim};
    border: 1px solid ${palette.success}33;
    color: ${palette.success}CC;
  }

  .cb-box-warning {
    background: ${palette.warningDim};
    border: 1px solid ${palette.warning}33;
    color: ${palette.warning}CC;
  }

  .cb-box-danger {
    background: ${palette.dangerDim};
    border: 1px solid ${palette.danger}33;
    color: ${palette.danger}CC;
  }

  /* Grid cards */
  .cb-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 14px 0 4px;
  }

  @media (max-width: 520px) { .cb-grid { grid-template-columns: 1fr; } }

  .cb-card {
    background: ${palette.raised};
    border: 1px solid ${palette.border};
    border-radius: 12px;
    padding: 13px 15px;
    display: flex;
    align-items: flex-start;
    gap: 11px;
  }

  .cb-card-icon {
    width: 32px; height: 32px; min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .cb-card strong {
    font-size: 13px;
    font-weight: 600;
    color: ${palette.text};
    display: block;
    margin-bottom: 3px;
  }

  .cb-card p {
    font-size: 12px;
    color: ${palette.muted};
    line-height: 1.5;
    margin: 0 !important;
  }

  /* Contact */
  .cb-contact {
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}33;
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    margin-top: 36px;
  }

  .cb-contact h3 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .cb-contact p {
    color: ${palette.muted};
    font-size: 14px;
    margin-bottom: 20px;
  }

  .cb-contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: ${palette.accent};
    color: #0B0F1A;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 12px 24px;
    border-radius: 100px;
    text-decoration: none;
    transition: opacity .2s, transform .2s;
  }

  .cb-contact-btn:hover { opacity: .87; transform: translateY(-1px); }

  /* Footer */
  .cb-footer {
    text-align: center;
    padding-top: 28px;
    color: ${palette.faint};
    font-size: 12px;
  }

  @keyframes cb-fadeup {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// ── Icon helpers ──────────────────────────────────────────────────────────
const Ico = ({ e }) => <span role="img" aria-hidden="true">{e}</span>;

function SecIcon({ bg, children }) {
  return <div className="cb-icon" style={{ background: bg }}>{children}</div>;
}

function Section({ iconBg, icon, title, children, delay = 0 }) {
  return (
    <div className="cb-section" style={{ animationDelay: `${delay}s` }}>
      <div className="cb-section-head">
        <SecIcon bg={iconBg}><Ico e={icon} /></SecIcon>
        <h2>{title}</h2>
      </div>
      {children}
    </div>
  );
}

function SuccessBox({ children }) {
  return <div className="cb-box cb-box-success"><Ico e="✓" /><span>{children}</span></div>;
}

function WarnBox({ children }) {
  return <div className="cb-box cb-box-warning"><Ico e="⚠" /><span>{children}</span></div>;
}

function InfoCard({ icon, iconBg, title, desc }) {
  return (
    <div className="cb-card">
      <div className="cb-card-icon" style={{ background: iconBg }}>
        <Ico e={icon} />
      </div>
      <div>
        <strong>{title}</strong>
        <p>{desc}</p>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────
export default function AnimalEscapePrivacyPolicy() {
  const [year] = useState(new Date().getFullYear());

  return (
    <>
      <style>{css}</style>
      <div className="cb-pp">
        <div className="cb-bars-bg" />
        <div className="cb-glow" />

        <div className="cb-wrap">

          {/* Hero */}
          <header className="cb-hero">
            <div className="cb-badge">🔐 &nbsp;Legal document</div>
            <h1>Privacy <em>Policy</em></h1>
            <p className="cb-hero-sub">
              How <strong>Animal Escape</strong> collects, uses, and protects your information.
            </p>
            <div className="cb-meta">
              <span>🎮 Developer: AlphaY</span>
              <span>📅 Effective: June 25, {year}</span>
              <span>📱 Free app · Ad supported</span>
            </div>
          </header>

          <div className="cb-divider" />

          {/* Overview */}
          <Section iconBg={palette.accentDim} icon="📄" title="Overview" delay={0.05}>
            <p>
              <strong style={{ color: palette.text }}>AlphaY</strong> built Animal Escape as a free,
              ad-supported mobile puzzle game. This page explains what information we collect when
              you play, how we use it, and your rights.
            </p>
            <p>By downloading and playing Animal Escape, you agree to the practices described below.</p>
            <SuccessBox>
              We never sell your personal data. Animal Escape requires no account, no sign-in,
              and collects no sensitive personal information.
            </SuccessBox>
          </Section>

          {/* Data Collected */}
          <Section iconBg={palette.infoDim} icon="🗂️" title="Information we collect" delay={0.10}>
            <p>
              <strong style={{ color: palette.text }}>We do not collect personally identifiable information.</strong>{" "}
              Animal Escape does not require an account, name, email, or any profile data.
            </p>
            <p>The following non-personal data may be collected automatically:</p>
            <ul>
              <li>Device type and operating system version</li>
              <li>App version and language settings</li>
              <li>General usage patterns (levels played, session duration)</li>
              <li>Crash and error logs to help us identify and fix bugs</li>
              <li>IP address — used by ad providers for region-based ad serving only</li>
            </ul>
            <p style={{ marginTop: 12 }}>
              Your game progress (current level, coins, unlocked animals, hint count) is stored{" "}
              <strong style={{ color: palette.text }}>locally on your device only</strong> via
              Android SharedPreferences. It is never transmitted to any server.
            </p>
            <SuccessBox>
              Uninstalling the app permanently deletes all locally stored game data from your device.
            </SuccessBox>
          </Section>

          {/* Advertising */}
          <Section iconBg={palette.warningDim} icon="📢" title="Advertising" delay={0.15}>
            <p>
              Animal Escape is free to play and is supported by in-app advertising served by{" "}
              <strong style={{ color: palette.text }}>Google AdMob</strong>. AdMob may collect
              and use data to serve personalised or non-personalised ads, including:
            </p>
            <ul>
              <li>Android Advertising ID (AAID)</li>
              <li>Approximate location (country / region)</li>
              <li>Device information and in-app interaction data</li>
            </ul>
            <WarnBox>
              Ad data is collected and processed by Google AdMob, not by us. Their privacy
              policy governs that data:{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                policies.google.com/privacy
              </a>
            </WarnBox>
            <p style={{ marginTop: 12 }}>
              You can opt out of personalised ads at any time via{" "}
              <strong style={{ color: palette.text }}>
                Settings → Google → Ads → Opt out of ads personalisation
              </strong>{" "}
              on your device.
            </p>
          </Section>

          {/* Third Parties */}
          <Section iconBg={palette.successDim} icon="🔌" title="Third-party services" delay={0.20}>
            <p>Animal Escape uses the following third-party services, each governed by its own privacy policy:</p>
            <div className="cb-grid">
              <InfoCard
                icon="📢" iconBg={palette.warningDim}
                title="Google AdMob"
                desc="In-app advertising. May collect device and usage data for ad targeting."
              />
              <InfoCard
                icon="▶️" iconBg="#0A1A10"
                title="Google Play Services"
                desc="App distribution, updates, and crash reporting on Android."
              />
              <InfoCard
                icon="📳" iconBg={palette.infoDim}
                title="Vibration plugin"
                desc="Accesses device vibration motor only. No data collected or transmitted."
              />
              <InfoCard
                icon="🔊" iconBg="#1A100A"
                title="flame_audio"
                desc="Plays local audio assets only. No network calls or data collection."
              />
              <InfoCard
                icon="💾" iconBg={palette.successDim}
                title="SharedPreferences"
                desc="Local on-device storage only. Your game data never leaves your device."
              />
              <InfoCard
                icon="🎮" iconBg={palette.accentDim}
                title="Flame game engine"
                desc="Open-source Flutter game engine. No data collected by the engine itself."
              />
            </div>
          </Section>

          {/* Data Retention */}
          <Section iconBg="#0A1525" icon="🕓" title="Data retention" delay={0.25}>
            <p>
              Game progress stored locally on your device is kept until you uninstall the app,
              at which point Android automatically deletes it.
            </p>
            <p>
              We do not maintain any server-side database of your personal game data. Crash logs
              sent to Google Play Services are retained for a limited period under Google's own policy.
            </p>
          </Section>

          {/* Children */}
          <Section iconBg={palette.dangerDim} icon="🧒" title="Children's privacy" delay={0.30}>
            <p>
              Animal Escape is a casual puzzle game designed for all ages. We do not knowingly collect
              personal information from children under 13 (or under 16 in the EU/EEA).
            </p>
            <p>
              The game displays ads via Google AdMob. Parents should be aware that AdMob may serve
              ads to younger players. To disable personalised ads on a child's device, go to{" "}
              <strong style={{ color: palette.text }}>Settings → Google → Ads</strong>.
            </p>
            <p>
              If you believe a child has provided personal information through the app, please contact
              us at{" "}
              <a href="mailto:alphay28071994@gmail.com">alphay28071994@gmail.com</a>{" "}
              and we will remove it promptly.
            </p>
          </Section>

          {/* Security */}
          <Section iconBg={palette.successDim} icon="🛡️" title="Security" delay={0.35}>
            <p>
              Since Animal Escape does not transmit personal data to external servers, your primary
              security consideration is your local device security. We recommend keeping your
              device OS and the app up to date.
            </p>
            <p>
              Crash reports sent to Google Play Services are transmitted over HTTPS and handled
              under Google's security standards.
            </p>
          </Section>

          {/* Changes */}
          <Section iconBg={palette.warningDim} icon="🔄" title="Changes to this policy" delay={0.40}>
            <p>
              We may update this privacy policy from time to time. Changes will be posted on this
              page with an updated effective date at the top.
            </p>
            <p>
              We encourage you to review this policy periodically. Continued use of Animal Escape
              after changes are posted constitutes acceptance of the updated policy.
            </p>
          </Section>

          {/* Consent */}
          <Section iconBg={palette.successDim} icon="✅" title="Your consent" delay={0.45}>
            <p>
              By downloading and playing Animal Escape, you consent to this privacy policy. If you
              do not agree, please discontinue use of the app and uninstall it from your device.
            </p>
          </Section>

          {/* Contact */}
          <div className="cb-contact">
            <h3>Questions or concerns?</h3>
            <p>
              If you have any questions about how Animal Escape handles your data, don't hesitate
              to get in touch.
            </p>
            <a href="mailto:alphay28071994@gmail.com" className="cb-contact-btn">
              ✉️ &nbsp;alphay28071994@gmail.com
            </a>
          </div>

          {/* Footer */}
          <div className="cb-divider" />
          <footer className="cb-footer">
            <p>© {year} <strong style={{ color: palette.muted }}>AlphaY</strong> · Animal Escape · All rights reserved.</p>
            <p style={{ marginTop: 5 }}>This app is not affiliated with or endorsed by Google LLC.</p>
          </footer>

        </div>
      </div>
    </>
  );
}